/**
 * ConfigOwnerCallout — evita editar la misma palanca en dos pages.
 *
 * Muestra dueño canónico + prioridad de resolución en runtime.
 * Un solo dashboard Vite; dos pipelines de producto (edge vs DualCTA).
 */

import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

export type ConfigOwnerCalloutProps = {
  /** Qué controla esta page (corto). */
  owns: string;
  /** Dónde NO editar (o qué se pisa). */
  notHere?: string;
  /** Link al dueño real si esta page es solo fallback. */
  ownerPath?: string;
  ownerLabel?: string;
  /** Escalon de prioridad legible (arriba = gana). */
  priority?: string[];
  /** fallback | owner | info */
  variant?: "owner" | "fallback" | "info";
  className?: string;
};

export function ConfigOwnerCallout({
  owns,
  notHere,
  ownerPath,
  ownerLabel,
  priority,
  variant = "owner",
  className,
}: ConfigOwnerCalloutProps) {
  const isFallback = variant === "fallback";
  const isInfo = variant === "info";

  return (
    <div
      className={cn(
        "rounded-2xl border px-4 py-3 text-[12px] leading-relaxed",
        isFallback
          ? "border-amber-500/30 bg-amber-500/[0.07] text-amber-100/90"
          : isInfo
            ? "border-sky-500/25 bg-sky-500/[0.06] text-sky-100/90"
            : "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-100/90",
        className,
      )}
      role="note"
    >
      <div className="flex items-start gap-2.5">
        {isFallback ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        ) : isInfo ? (
          <Layers className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
        ) : (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
        )}
        <div className="min-w-0 space-y-1.5">
          <p>
            <span className="font-semibold text-white/80">
              {isFallback ? "Fallback / secundario:" : "Dueño canónico:"}
            </span>{" "}
            <span className="text-white/60">{owns}</span>
          </p>
          {notHere ? (
            <p className="text-white/45">
              <span className="font-medium text-white/55">No pisa:</span> {notHere}
            </p>
          ) : null}
          {priority && priority.length > 0 ? (
            <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-[11px] text-white/40">
              {priority.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : null}
          {ownerPath && ownerLabel ? (
            <Link
              to={ownerPath}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-300/95 underline-offset-2 hover:underline"
            >
              Ir a {ownerLabel}
              <ArrowRight className="h-3 w-3" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
